/**
 * OfflineBanner — top-of-page amber banner displayed when the device is
 * offline or the SSE stream has been silent for >30 s.
 *
 * Shows:
 *   "Showing data from {ago}" — real timestamp via Intl.RelativeTimeFormat
 *   "Try again" CTA — invalidates ['outbreak'] query, prompting a refetch
 *
 * Rules:
 *   - RTL: logical properties only (ps-*, pe-*, no left/right).
 *   - Warning palette: amber-50/amber-900 (light), amber-900/20 + amber-300 (dark).
 *   - No animate-pulse. No transition-all. transition-colors duration-150 only.
 *   - Touch target ≥44×44 via p-3 on the Try again button.
 *   - `aria-live="polite"` so screen readers announce without interrupting.
 *   - Respect safe-area-inset-top on iOS via padding-top env(safe-area-inset-top).
 */
import type React from "react";
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { TranslateFn } from "#/lib/i18n.js";

// ---------------------------------------------------------------------------
// Icons (inline SVG — no external icon library in this project)
// ---------------------------------------------------------------------------

function WifiOffIcon() {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="2" y1="2" x2="22" y2="22" />
      <path d="M8.5 16.5a5 5 0 0 1 7 0" />
      <path d="M2 8.82a15 15 0 0 1 4.17-2.65" />
      <path d="M10.66 5c4.01-.36 8.14.9 11.34 3.76" />
      <path d="M16.85 11.25a10 10 0 0 1 2.22 1.68" />
      <path d="M5 13a10 10 0 0 1 5.24-2.76" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M23 4v6h-6" />
      <path d="M20.49 15A9 9 0 1 1 18.36 5.64L23 10" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Relative time formatter
// ---------------------------------------------------------------------------

/**
 * Format a Unix ms timestamp as a relative string using Intl.RelativeTimeFormat.
 * Returns "just now" if within 30 s, otherwise e.g. "12 minutes ago".
 * The `locale` is the i18n locale string ("en", "ar", "fr").
 */
function formatRelativeTime(fetchedAt: number, locale: string): string {
  const diffMs = Date.now() - fetchedAt;
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 30) return locale === "ar" ? "منذ لحظات" : locale === "fr" ? "il y a quelques secondes" : "just now";

  let value: number;
  let unit: Intl.RelativeTimeFormatUnit;

  if (diffSec < 3600) {
    value = -Math.floor(diffSec / 60);
    unit = "minute";
  } else if (diffSec < 86400) {
    value = -Math.floor(diffSec / 3600);
    unit = "hour";
  } else {
    value = -Math.floor(diffSec / 86400);
    unit = "day";
  }

  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    return rtf.format(value, unit);
  } catch {
    // Fallback if locale is not supported by Intl
    const abs = Math.abs(value);
    return `${abs} ${unit}${abs !== 1 ? "s" : ""} ago`;
  }
}

// ---------------------------------------------------------------------------
// OfflineBanner
// ---------------------------------------------------------------------------

export interface OfflineBannerProps {
  /** Whether we are in degraded (offline or SSE disconnected) state. */
  isDegraded: boolean;
  /** Unix ms timestamp of the last cached fetch. Null = no cache. */
  cachedAt: number | null;
  /** i18n locale for Intl.RelativeTimeFormat. */
  locale: string;
  /** Translation function. */
  t: TranslateFn;
  /** data-testid for Playwright targeting. */
  "data-testid"?: string;
}

export function OfflineBanner({
  isDegraded,
  cachedAt,
  locale,
  t,
  "data-testid": testId = "offline-banner",
}: OfflineBannerProps): React.ReactElement | null {
  const queryClient = useQueryClient();

  const handleRetry = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["outbreak"] });
  }, [queryClient]);

  if (!isDegraded) return null;

  const agoText = cachedAt
    ? t("dashboard:offline.showingDataFrom", {
        ago: formatRelativeTime(cachedAt, locale),
      })
    : t("dashboard:offline.noCache");

  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid={testId}
      className={[
        "sticky top-0 z-50 flex items-center justify-between gap-3",
        "border-b border-amber-200 dark:border-amber-800",
        "bg-amber-50 dark:bg-amber-900/20",
        "text-amber-900 dark:text-amber-300",
        "ps-4 pe-4 py-2 min-h-[44px] text-sm",
      ].join(" ")}
      style={{ paddingBlockStart: "max(0.5rem, env(safe-area-inset-top))" }}
    >
      <div className="flex items-center gap-2">
        <WifiOffIcon />
        <span>{agoText}</span>
      </div>
      {/* Touch target ≥44×44 via p-3 */}
      <button
        type="button"
        onClick={handleRetry}
        data-testid="offline-banner-retry"
        className={[
          "inline-flex items-center gap-1.5 rounded-md",
          "border border-amber-300 dark:border-amber-700",
          "bg-amber-50 dark:bg-amber-900/30",
          "px-3 py-3 text-xs font-medium",
          "hover:bg-amber-100 dark:hover:bg-amber-800/40",
          "focus-visible:outline-none focus-visible:ring-2",
          "transition-colors duration-150",
        ].join(" ")}
        style={{ minHeight: "44px", minWidth: "44px" }}
        aria-label={t("dashboard:offline.retryAriaLabel")}
      >
        <RefreshIcon />
        {t("dashboard:offline.retry")}
      </button>
    </div>
  );
}
